import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useIngredientConsumption, useIngredientPlateBreakdown } from '@/hooks/useInventoryInsights'
import type { IngredientConsumption } from '@/hooks/useInventoryInsights'
import { useInventory } from '@/hooks/useInventory'
import { Badge } from '@/components/ui/badge'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TrendingDown, Calendar, UtensilsCrossed } from 'lucide-react'
import { IngredientIcon } from '@/components/ui/ingredient-icon'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'

interface InventoryEvolutionChartProps {
  restaurantId: string | null
}

export function InventoryEvolutionChart({ restaurantId }: InventoryEvolutionChartProps) {
  const { t, i18n } = useTranslation()
  const [timePeriod, setTimePeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('daily')
  const [selectedIngredient, setSelectedIngredient] = useState<IngredientConsumption | null>(null)

  const { data: consumption, isLoading } = useIngredientConsumption(restaurantId, timePeriod)
  const { data: inventory } = useInventory(restaurantId)

  const negativeStockIds = useMemo(() => {
    const ids = new Set<string>()
    inventory?.forEach((inv) => {
      if (inv.quantity < 0) ids.add(inv.ingredientId)
    })
    return ids
  }, [inventory])
  const { data: plateBreakdown, isLoading: isLoadingBreakdown } = useIngredientPlateBreakdown(
    restaurantId,
    selectedIngredient?.ingredientId,
    timePeriod,
    !!selectedIngredient
  )

  const timePeriodLabel = timePeriod === 'daily'
    ? t('inventory.insights.today')
    : timePeriod === 'weekly'
      ? t('inventory.insights.thisWeek')
      : timePeriod === 'monthly'
        ? t('inventory.insights.thisMonth')
        : t('inventory.insights.thisYear')

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t('inventory.insights.evolution')}</h3>
          <div className="w-32 h-10 bg-muted animate-pulse rounded" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted animate-pulse rounded w-24" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted animate-pulse rounded w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (!consumption || consumption.length === 0) {
    return (
      <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{t('inventory.insights.evolution')}</h3>
        <div className="flex flex-wrap gap-2">
          <Badge
            variant={timePeriod === 'daily' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => setTimePeriod('daily')}
          >
            {t('inventory.insights.daily')}
          </Badge>
          <Badge
            variant={timePeriod === 'weekly' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => setTimePeriod('weekly')}
          >
            {t('inventory.insights.weekly')}
          </Badge>
          <Badge
            variant={timePeriod === 'monthly' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => setTimePeriod('monthly')}
          >
            {t('inventory.insights.monthly')}
          </Badge>
          <Badge
            variant={timePeriod === 'yearly' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => setTimePeriod('yearly')}
          >
            {t('inventory.insights.yearly')}
          </Badge>
        </div>
      </div>
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-muted-foreground">
              {t('inventory.insights.noConsumptionData')}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <TrendingDown className="h-5 w-5" />
          {t('inventory.insights.evolution')}
        </h3>
        <div className="flex flex-wrap gap-2">
          <Badge
            variant={timePeriod === 'daily' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => setTimePeriod('daily')}
          >
            {t('inventory.insights.daily')}
          </Badge>
          <Badge
            variant={timePeriod === 'weekly' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => setTimePeriod('weekly')}
          >
            {t('inventory.insights.weekly')}
          </Badge>
          <Badge
            variant={timePeriod === 'monthly' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => setTimePeriod('monthly')}
          >
            {t('inventory.insights.monthly')}
          </Badge>
          <Badge
            variant={timePeriod === 'yearly' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => setTimePeriod('yearly')}
          >
            {t('inventory.insights.yearly')}
          </Badge>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {consumption.map((item) => {
          const isNegativeStock = negativeStockIds.has(item.ingredientId)
          return (
            <Card
              key={item.ingredientId}
              className={`cursor-pointer transition-colors ${
                isNegativeStock
                  ? 'border-red-400 bg-red-50/50 hover:bg-red-100/50 dark:bg-red-950/20 dark:hover:bg-red-950/40 dark:border-red-800'
                  : 'hover:bg-accent/50'
              }`}
              onClick={() => setSelectedIngredient(item)}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <IngredientIcon
                    ingredient={{
                      nameAr: item.nameAr,
                      nameFr: item.nameFr,
                      emoji: item.emoji
                    }}
                    size={24}
                    className="flex-shrink-0"
                  />
                  <span>{i18n.language === 'ar' ? item.nameAr : item.nameFr}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className={`text-2xl font-bold ${isNegativeStock ? 'text-red-600 dark:text-red-400' : ''}`}>
                      {isNegativeStock ? '-' : ''}{item.totalConsumed.toFixed(2)}
                    </span>
                    <span className="text-sm text-muted-foreground">{item.unit}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>
                      {timePeriod === 'daily' && t('inventory.insights.today')}
                      {timePeriod === 'weekly' && t('inventory.insights.thisWeek')}
                      {timePeriod === 'monthly' && t('inventory.insights.thisMonth')}
                      {timePeriod === 'yearly' && t('inventory.insights.thisYear')}
                    </span>
                  </div>
                  {item.averagePerDay > 0 && (
                    <div className={`text-xs ${isNegativeStock ? 'text-red-500 dark:text-red-400' : 'text-muted-foreground'}`}>
                      {t('inventory.insights.avgPerDay')}: {isNegativeStock ? '-' : ''}{item.averagePerDay.toFixed(2)} {item.unit}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Plate Breakdown Sheet */}
      <Sheet open={!!selectedIngredient} onOpenChange={(open) => { if (!open) setSelectedIngredient(null) }}>
        <SheetContent side="bottom" className="max-h-[80vh]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {selectedIngredient && (
                <IngredientIcon
                  ingredient={{
                    nameAr: selectedIngredient.nameAr,
                    nameFr: selectedIngredient.nameFr,
                    emoji: selectedIngredient.emoji
                  }}
                  size={24}
                  className="flex-shrink-0"
                />
              )}
              <span>
                {selectedIngredient && (i18n.language === 'ar' ? selectedIngredient.nameAr : selectedIngredient.nameFr)}
              </span>
              <Badge variant="secondary" className="text-xs">
                {timePeriodLabel}
              </Badge>
            </SheetTitle>
            <SheetDescription>
              {t('inventory.insights.plateBreakdownDesc')}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-3">
            {isLoadingBreakdown ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg border">
                    <div className="space-y-2 flex-1">
                      <div className="h-4 bg-muted animate-pulse rounded w-32" />
                      <div className="h-3 bg-muted animate-pulse rounded w-24" />
                    </div>
                    <div className="h-6 bg-muted animate-pulse rounded w-16" />
                  </div>
                ))}
              </div>
            ) : !plateBreakdown || plateBreakdown.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <UtensilsCrossed className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>{t('inventory.insights.noPlateData')}</p>
              </div>
            ) : (
              <>
                {plateBreakdown.map((plate) => {
                  const plateName = i18n.language === 'ar' ? plate.nameAr : plate.nameFr
                  const optionName = plate.optionId
                    ? (i18n.language === 'ar' ? plate.optionNameAr : plate.optionNameFr)
                    : null
                  return (
                    <div
                      key={`${plate.menuItemId}:${plate.optionId || 'base'}`}
                      className="flex items-center justify-between p-3 rounded-lg border"
                    >
                      <div className="space-y-1 flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{plateName}</p>
                          {optionName && (
                            <Badge variant="outline" className="text-xs shrink-0">
                              {optionName}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {plate.ingredientPerPlate} {plate.unit} {t('inventory.insights.perPlate')}
                          {' · '}
                          x{plate.totalOrdered} {t('inventory.insights.ordered')}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0 ms-3">
                        <p className="font-semibold">
                          {plate.totalIngredientConsumed.toFixed(2)} {plate.unit}
                        </p>
                      </div>
                    </div>
                  )
                })}

                {/* Total row */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border-t-2">
                  <p className="font-semibold">{t('inventory.insights.total')}</p>
                  <p className="font-bold">
                    {selectedIngredient?.totalConsumed.toFixed(2)} {selectedIngredient?.unit}
                  </p>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
